import { FC } from 'react';

import {
  Col,
  Divider,
  Flex,
  Form,
  Input,
  InputProps,
  Row,
  Typography,
} from 'antd';
import { FormInstance } from 'antd/lib';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { StorageSystemForm } from '@/types/storageSystem';
import { normalizePositiveNumericValuesInput } from '@/utils';

type Props = {
  disabled?: boolean;
  form: FormInstance<StorageSystemForm>;
};

const BatteryCapability: FC<Props> = ({ disabled = false, form }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const onChangeRatedEnergy: InputProps['onChange'] = (event) => {
    const val = event.target.value;
    const numericValue = val ? parseFloat(val) : null;

    if (numericValue !== null) {
      form.setFieldsValue({
        internalBatteryVoltageV: undefined,
        ratedCapacityAh: undefined,
      });
    }
  };

  const onChangeRatedEnergyParameter = () => {
    const { ratedCapacityAh, internalBatteryVoltageV } = form.getFieldsValue();
    const calculatedRatedEnergy =
      ((ratedCapacityAh || 0) * (internalBatteryVoltageV || 0)) / 1e6;
    form.setFieldsValue({ ratedEnergyMwh: calculatedRatedEnergy });
  };

  const onChangeRoundTripEfficiencyParameter = () => {
    const {
      inverterEfficiency,
      powerConversionUnitEfficiency,
      batteryRoundTripEfficiency,
    } = form.getFieldsValue();

    const calculatedRoundTripEfficiency = (
      ((inverterEfficiency || 0) *
        (powerConversionUnitEfficiency || 0) *
        (batteryRoundTripEfficiency || 0)) /
      (100 * 100)
    ).toFixed(2);

    form.setFieldsValue({
      netOverallRoundTripEfficiency: Number(calculatedRoundTripEfficiency),
    });
  };

  const onChangeRoundTripEfficiency = () => {
    form.setFieldsValue({
      inverterEfficiency: undefined,
      powerConversionUnitEfficiency: undefined,
      batteryRoundTripEfficiency: undefined,
    });
  };

  return (
    <Form.Item
      wrapperCol={{ span: 24 }}
      style={{
        border: '0.5px solid',
        borderRadius: '10px',
        borderColor: theme.colors.grayLightAccent,
      }}
      className="p-16"
    >
      <Row>
        <Col className="pr-8" span={12}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="ratedPowerMw"
            label={t('storageSystem.form.batteryCapability.ratedPower')}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.batteryCapability.ratedPowerRequired'
                ),
              },
            ]}
          >
            <Input
              addonAfter="MW"
              disabled={disabled}
              placeholder={t('storageSystem.form.batteryCapability.ratedPower')}
            />
          </Form.Item>
        </Col>
        <Col className="pl-8" span={12}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="minAllowedSoh"
            label={t('storageSystem.form.batteryCapability.minAllowedSoh')}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.batteryCapability.minAllowedSohRequired'
                ),
              },
              {
                validator(_, value) {
                  const maxValue = parseFloat(value);
                  if (isNaN(maxValue) || maxValue < 1) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.batteryCapability.valueBetween1And99'
                        )
                      )
                    );
                  } else if (maxValue > 99) {
                    form.setFieldsValue({ minAllowedSoh: 99 });
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              className="w-100"
              addonAfter="%"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.minAllowedSoh'
              )}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={8}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="ratedCapacityAh"
            label={t('storageSystem.form.batteryCapability.ratedCapacity')}
            normalize={normalizePositiveNumericValuesInput}
          >
            <Input
              onChange={onChangeRatedEnergyParameter}
              addonAfter="Ah"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.ratedCapacity'
              )}
            />
          </Form.Item>
        </Col>
        <Flex className="pl-8 pr-8" vertical align="center" justify="center">
          <Typography.Text
            className="fs-16 pt-8"
            style={{ color: theme.colors.bluePrimary }}
          >
            X
          </Typography.Text>
        </Flex>

        <Col span={8}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="internalBatteryVoltageV"
            label={t(
              'storageSystem.form.batteryCapability.internalBatteryVoltage'
            )}
            normalize={normalizePositiveNumericValuesInput}
          >
            <Input
              onChange={onChangeRatedEnergyParameter}
              addonAfter="V"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.internalBatteryVoltage'
              )}
            />
          </Form.Item>
        </Col>
        <Flex className="pl-8 pr-8" vertical align="center" justify="center">
          <Typography.Text
            className="fs-24 pt-8"
            style={{ color: theme.colors.bluePrimary }}
          >
            =
          </Typography.Text>
        </Flex>
        <Col flex="auto">
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="ratedEnergyMwh"
            label={t('storageSystem.form.batteryCapability.ratedEnergy')}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.batteryCapability.ratedEnergyRequired'
                ),
              },
            ]}
          >
            <Input
              onChange={onChangeRatedEnergy}
              addonAfter="MWh"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.ratedEnergy'
              )}
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider
        style={{
          border: '0.5px solid',
          borderColor: theme.colors.grayLightAccent,
        }}
        className="mt-24 mb-24"
      />
      <Row>
        <Col className="pr-8" span={12}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="minAllowedStateOfCharge"
            label={t(
              'storageSystem.form.batteryCapability.minAllowedStateOfCharge'
            )}
            normalize={normalizePositiveNumericValuesInput}
            dependencies={['maxAllowedStateOfCharge']}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.batteryCapability.minAllowedStateOfChargeRequired'
                ),
              },
              {
                validator(_, value) {
                  const minValue = parseFloat(value);
                  if (isNaN(minValue) || minValue < 0) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.batteryCapability.valueBetween0And100'
                        )
                      )
                    );
                  } else if (minValue > 100) {
                    form.setFieldsValue({ minAllowedStateOfCharge: 100 });
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const maxAllowedStateOfCharge = parseFloat(
                    getFieldValue('maxAllowedStateOfCharge')
                  );
                  const minAllowedStateOfCharge = parseFloat(value);
                  if (minAllowedStateOfCharge >= maxAllowedStateOfCharge) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.batteryCapability.minCannotBeGreaterThanMax'
                        )
                      )
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input
              className="w-100"
              addonAfter="%"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.minAllowedStateOfCharge'
              )}
            />
          </Form.Item>
        </Col>
        <Col className="pl-8" span={12}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="maxAllowedStateOfCharge"
            label={t(
              'storageSystem.form.batteryCapability.maxAllowedStateOfCharge'
            )}
            dependencies={['minAllowedStateOfCharge']}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.batteryCapability.maxAllowedStateOfChargeRequired'
                ),
              },
              {
                validator(_, value) {
                  const maxValue = parseFloat(value);
                  if (isNaN(maxValue) || maxValue < 0) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.batteryCapability.valueBetween0And100'
                        )
                      )
                    );
                  } else if (maxValue > 100) {
                    form.setFieldsValue({ maxAllowedStateOfCharge: 100 });
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const minAllowedStateOfCharge = parseFloat(
                    getFieldValue('minAllowedStateOfCharge')
                  );
                  const maxAllowedStateOfCharge = parseFloat(value);
                  if (maxAllowedStateOfCharge <= minAllowedStateOfCharge) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.batteryCapability.maxCannotBeLessThanMin'
                        )
                      )
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input
              className="w-100"
              addonAfter="%"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.maxAllowedStateOfCharge'
              )}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={5}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="batteryRoundTripEfficiency"
            label={t(
              'storageSystem.form.batteryCapability.batteryRoundTripEfficiency'
            )}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                validator(_, value) {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }

                  const maxValue = parseFloat(value);
                  if (isNaN(maxValue) || maxValue < 0) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.batteryCapability.valueBetween0And100'
                        )
                      )
                    );
                  } else if (maxValue > 100) {
                    form.setFieldsValue({ batteryRoundTripEfficiency: 100 });
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              onChange={onChangeRoundTripEfficiencyParameter}
              className="w-100"
              addonAfter="%"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.batteryRoundTripEfficiency'
              )}
            />
          </Form.Item>
        </Col>
        <Flex className="pl-8 pr-8" vertical align="center" justify="center">
          <Typography.Text
            className="fs-16 pt-8"
            style={{ color: theme.colors.bluePrimary }}
          >
            X
          </Typography.Text>
        </Flex>
        <Col span={6}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="powerConversionUnitEfficiency"
            label={t(
              'storageSystem.form.batteryCapability.powerConversionUnitEfficiency'
            )}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                validator(_, value) {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }

                  const maxValue = parseFloat(value);
                  if (isNaN(maxValue) || maxValue < 0) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.batteryCapability.valueBetween0And100'
                        )
                      )
                    );
                  } else if (maxValue > 100) {
                    form.setFieldsValue({ powerConversionUnitEfficiency: 100 });
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              onChange={onChangeRoundTripEfficiencyParameter}
              className="w-100"
              addonAfter="%"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.powerConversionUnitEfficiency'
              )}
            />
          </Form.Item>
        </Col>
        <Flex className="pl-8 pr-8" vertical align="center" justify="center">
          <Typography.Text
            className="fs-16 pt-8"
            style={{ color: theme.colors.bluePrimary }}
          >
            X
          </Typography.Text>
        </Flex>
        <Col span={5}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="inverterEfficiency"
            label={t('storageSystem.form.batteryCapability.inverterEfficiency')}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                validator(_, value) {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }

                  const maxValue = parseFloat(value);
                  if (isNaN(maxValue) || maxValue < 0) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.batteryCapability.valueBetween0And100'
                        )
                      )
                    );
                  } else if (maxValue > 100) {
                    form.setFieldsValue({ inverterEfficiency: 100 });
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              onChange={onChangeRoundTripEfficiencyParameter}
              className="w-100"
              addonAfter="%"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.inverterEfficiency'
              )}
            />
          </Form.Item>
        </Col>
        <Flex className="pl-8 pr-8" vertical align="center" justify="center">
          <Typography.Text
            className="fs-24 pt-8"
            style={{ color: theme.colors.bluePrimary }}
          >
            =
          </Typography.Text>
        </Flex>
        <Col span={6}>
          <Form.Item<StorageSystemForm>
            wrapperCol={{ span: 24 }}
            name="netOverallRoundTripEfficiency"
            label={t(
              'storageSystem.form.batteryCapability.netOverallRoundTripEfficiency'
            )}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'storageSystem.form.batteryCapability.netOverallRoundTripEfficiencyRequired'
                ),
              },
              {
                validator(_, value) {
                  const maxValue = parseFloat(value);
                  if (isNaN(maxValue) || maxValue < 0) {
                    return Promise.reject(
                      new Error(
                        t(
                          'storageSystem.form.batteryCapability.valueBetween0And100'
                        )
                      )
                    );
                  } else if (maxValue > 100) {
                    form.setFieldsValue({ netOverallRoundTripEfficiency: 100 });
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              onChange={onChangeRoundTripEfficiency}
              className="w-100"
              addonAfter="%"
              disabled={disabled}
              placeholder={t(
                'storageSystem.form.batteryCapability.netOverallRoundTripEfficiency'
              )}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form.Item>
  );
};

export default BatteryCapability;
